import React, { FC, ReactNode } from 'react';

import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from './header-controls.module.scss';

import { Search } from 'views/components/Search';
import { Button, Filter, SearchIcon } from 'views/components/UI';

interface IHeaderSearch {
  value: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  collapsible?: boolean;
}

interface IHeaderFilterAction {
  onClick: () => void;
  labelId?: string;
  ariaLabelId?: string;
  showLabel?: boolean;
  className?: string;
}

interface IHeaderPrimaryAction {
  labelId: string;
  onClick?: () => void;
  disabled?: boolean;
}

interface IHeaderControls {
  search?: IHeaderSearch;
  beforeFilter?: ReactNode;
  filter?: IHeaderFilterAction;
  afterFilter?: ReactNode;
  primary?: ReactNode;
  primaryAction?: IHeaderPrimaryAction;
  filters?: ReactNode;
}

interface IHeaderControlsProps {
  controls: IHeaderControls;
  isSearchOpen: boolean;
  setIsSearchOpen: (isOpen: boolean) => void;
}

const HeaderControls: FC<IHeaderControlsProps> = ({
  controls,
  isSearchOpen,
  setIsSearchOpen,
}) => {
  const { search, beforeFilter, filter, afterFilter, primary, primaryAction, filters } =
    controls;
  const intl = useIntl();
  const isSearchCollapsible = search?.collapsible !== false;
  const showSearch = Boolean(search) && (!isSearchCollapsible || isSearchOpen);

  const clearSearch = (): void => {
    if (search?.onClear) {
      search.onClear();
    } else {
      search?.onChange('');
    }

    if (isSearchCollapsible) {
      setIsSearchOpen(false);
    }
  };

  const filterLabelId = filter?.labelId ?? 'filters.button';
  const filterAriaLabel = filter
    ? intl.formatMessage({ id: filter.ariaLabelId ?? filterLabelId })
    : undefined;

  return (
    <ul
      className={classNames(styles.controls, { [styles.filterLayout]: Boolean(filters) })}
    >
      {filters && <li className={styles.filters}>{filters}</li>}
      {beforeFilter && <li>{beforeFilter}</li>}
      {filter && (
        <li>
          <Button
            size="large"
            mode="tertiary"
            color="dark"
            icon={<Filter />}
            onClick={filter.onClick}
            className={filter.className}
            aria-label={filterAriaLabel}
          >
            {filter.showLabel === true && <FormattedMessage id={filterLabelId} />}
          </Button>
        </li>
      )}
      {afterFilter && <li>{afterFilter}</li>}
      {search && (
        <li className={classNames({ [styles.searchOpen]: showSearch })}>
          {showSearch ? (
            <Search
              searchValue={search.value}
              handleSearch={event => search.onChange(event.currentTarget.value)}
              handleClose={clearSearch}
            />
          ) : (
            <Button
              size="large"
              mode="tertiary"
              icon={<SearchIcon />}
              onClick={() => setIsSearchOpen(true)}
              aria-label={intl.formatMessage({ id: 'search.button.open' })}
            />
          )}
        </li>
      )}
      {primary ? (
        <li>{primary}</li>
      ) : (
        primaryAction && (
          <li>
            <Button onClick={primaryAction.onClick} disabled={primaryAction.disabled}>
              <FormattedMessage id={primaryAction.labelId} />
            </Button>
          </li>
        )
      )}
    </ul>
  );
};

export { HeaderControls };
export type { IHeaderControls };
